// RiskAssessmentsMantine.js
// Fixed version with working tabs and proper styling

import React, { useEffect, useState, useMemo } from 'react';
import StartAssessmentDialogV2 from './StartAssessmentDialogV2';
import {
  
  Title,
  Table,
  Group,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Tabs,
  TextInput,
  Select,
  
  RingProgress,
  Stack,
  Card,
  Loader,
  Center,
  Collapse,
  Avatar,
  ThemeIcon,
  Box,
  Container
} from '@mantine/core';
import {
  IconSearch,
  IconPlayerPlay,
  IconChevronDown,
  IconChevronUp,
  IconProgressCheck,
  IconAlertCircle,
  IconCircleCheck,
  IconHourglass,
  IconUser
} from '@tabler/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AssessmentReport from './AssessmentReport';
import { setModelId } from './redux/actions/riskActions';
import { positionToSearch } from './utils/assessmentPosition';

const RiskAssessmentsMantine = () => {
  const allModels = useSelector((state) => state.risks.models);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [expandedModel, setExpandedModel] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // Will be set by smart default logic
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [selectedModelForAssessment, setSelectedModelForAssessment] = useState(null);


  // Normalize status helper
  const normalizeStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'not started':
        return 'Not Started';
      case 'on-going':
      case 'ongoing':
        return 'On-Going';
      case 'completed':
        return 'Completed';
      default:
        return 'Not Started';
    }
  };

  // Enhanced progress calculation
  const calculateProgress = (model) => {
    if (!model.position) return 0;
    
    const stepWeights = {
      useCase: 20,
      safetyRisks: 50,
      securityAssessment: 20,
      compliance: 5,
      assessmentSummary: 5
    };

    const currentStep = model.position.majorStep;
    let progress = 0;

    const stepOrder = ['useCase', 'safetyRisks', 'securityAssessment', 'compliance', 'assessmentSummary'];
    const currentIndex = stepOrder.indexOf(currentStep);
    
    for (let i = 0; i < currentIndex; i++) {
      progress += stepWeights[stepOrder[i]];
    }

    if (currentStep === 'safetyRisks' && model.position.section) {
      progress += stepWeights[currentStep] * 0.3;
    }

    return Math.min(progress, 100);
  };

  // Enhanced models array with computed properties
  const modelsArray = useMemo(() => {
    const arr = Object.entries(allModels).map(([modelId, model]) => ({
      ...model,
      modelId,
      assessmentStatus: normalizeStatus(model.assessmentStatus),
      currentStep: model.step,
      position: model.position,
      progress: calculateProgress(model),
      lastActivity: model.updated_at || model.created_at,
      lastActivityBy: model.last_updated_by_name || model.last_updated_by_email || model.owner_name || model.owner_email || 'Unknown User'
    }));
  
    // Sort by selected criteria
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.modelName.localeCompare(b.modelName);
        case 'progress':
          return b.progress - a.progress;
        case 'status':
          return a.assessmentStatus.localeCompare(b.assessmentStatus);
        default: // created_at
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    return arr;
  }, [allModels, sortBy]);

  // Status counts
  const statusCounts = useMemo(() => ({
    all: modelsArray.length,
    ongoing: modelsArray.filter(m => m.assessmentStatus === 'On-Going').length,
    notstarted: modelsArray.filter(m => m.assessmentStatus === 'Not Started').length,
    completed: modelsArray.filter(m => m.assessmentStatus === 'Completed').length,
  }), [modelsArray]);

  // Smart default tab logic
  const getDefaultTab = useMemo(() => {
    if (statusCounts.notstarted > 0) return 'notstarted';
    if (statusCounts.ongoing > 0) return 'ongoing';
    return 'all';
  }, [statusCounts]);

  // Enhanced filtering
  const filteredModels = useMemo(() => {
    let filtered = modelsArray;

    // Filter by active tab
    if (activeTab !== 'all') {
      const statusMap = {
        'ongoing': 'On-Going',
        'notstarted': 'Not Started',
        'completed': 'Completed'
      };
      const mappedStatus = statusMap[activeTab];
      if (mappedStatus) {
        filtered = filtered.filter(model => model.assessmentStatus === mappedStatus);
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(model => 
        (model.modelName || model.model_name || '').toLowerCase().includes(query) ||
        model.assessmentStatus.toLowerCase().includes(query) ||
        (model.step && model.step.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [modelsArray, activeTab, searchQuery]);

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'Not Started': { color: 'gray', icon: IconHourglass },
      'On-Going': { color: 'blue', icon: IconProgressCheck },
      'Completed': { color: 'green', icon: IconCircleCheck }
    };

    const config = statusConfig[status] || statusConfig['Not Started'];
    const Icon = config.icon;

    return (
      <Badge 
        variant="light" 
        color={config.color} 
        size="sm"
        leftSection={<Icon size={12} />}
        radius="sm"
      >
        {status}
      </Badge>
    );
  };

  // Enhanced progress ring component
  const ProgressRing = ({ progress, size = 50 }) => {
    const color = progress === 100 ? 'green' : progress > 50 ? 'blue' : progress > 25 ? 'yellow' : 'red';
    
    return (
      <RingProgress
        size={size}
        thickness={4}
        roundCaps
        sections={[{ value: progress, color }]}
        label={
          <Text c={color} fw={700} ta="center" size="xs">
            {Math.round(progress)}%
          </Text>
        }
      />
    );
  };

  // Time since last activity
  const getTimeSinceActivity = (lastActivity) => {
    if (!lastActivity) return 'Unknown';
    
    const now = new Date();
    const last = new Date(lastActivity);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  // Position label helper
  const getPositionLabel = (position, step) => {
    if (!position && !step) return 'Not Started';
    
    const stepLabels = {
      useCase: 'Context',
      contextAssessment: 'Context',
      safetyRisks: 'Safety Risks',
      securityAssessment: 'Security',
      compliance: 'Compliance',
      assessmentSummary: 'Summary'
    };

    if (position) {
      const stepLabel = stepLabels[position.majorStep] || position.majorStep;
      if (position.section) {
        return `${stepLabel}`;
      }
      return stepLabel;
    }
    
    // Fallback to step if position not available
    if (step) {
      return stepLabels[step] || step.replace(/([A-Z])/g, ' $1').trim();
    }
    
    return 'Not Started';
  };

  // Handle model selection - now shows mode selection dialog
  const handleModelSelection = (model) => {
    // If it's a Not Started assessment, show mode selection dialog
    if (model.assessmentStatus === 'Not Started' || !model.assessmentStatus) {
      setSelectedModelForAssessment(model);
      setShowModeDialog(true);
      return;
    }

    // For ongoing assessments, continue directly to Co-pilot mode (existing behavior)
    resumeExistingAssessment(model);
  };

  // Resume existing ongoing assessment (preserves current behavior)
  const resumeExistingAssessment = async (model) => {
    dispatch(setModelId(model.modelId, model.modelName || model.model_name));

    try {
      const response = await fetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        body: JSON.stringify({ modelId: model.modelId, operation: 'fetch' }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        const assessmentUuid = result.assessmentUuid;

        let url = `/risk-form/${model.modelId}/${encodeURIComponent(model.modelName || model.model_name)}/${assessmentUuid}`;
        if (model.position && model.assessmentStatus === 'On-Going') {
          url += `?${positionToSearch(model.position).toString()}`;
        }
        navigate(url);
      } else {
        console.error('Failed to fetch existing assessment');
      }
    } catch (error) {
      console.error('Error resuming assessment:', error);
    }
  };

  useEffect(() => {
    if (allModels && Object.keys(allModels).length > 0) {
      setIsLoading(false);
    }
  }, [allModels]);

  // Set smart default tab when data loads
  useEffect(() => {
    if (!isLoading && activeTab === 'all') {
      const defaultTab = getDefaultTab;
      console.log('Setting smart default tab to:', defaultTab);
      setActiveTab(defaultTab);
    }
  }, [isLoading, getDefaultTab, activeTab]);

  if (isLoading) {
    return (
      <Center h={400}>
        <Stack align="center">
          <Loader size="lg" />
          <Text c="dimmed">Loading assessments...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Container size="xl" px="md">
      <Stack spacing="xl">
        {/* Enhanced Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" align="flex-start" mb="xl">
            <Group align="flex-start" spacing="md">
              <ThemeIcon size={56} radius="md" variant="light" color="blue">
                <IconProgressCheck size={28} />
              </ThemeIcon>
              <Box>
                <Title order={1} size="h2" weight={700} mb={4}>
                  Risk Assessments
                </Title>
                <Text size="md" c="dimmed">
                  Manage and track your AI model assessments
                </Text>
              </Box>
            </Group>
          </Group>

          {/* Search and Filters */}
          <Group spacing="md" mb="xl">
            <TextInput
              placeholder="Search assessments..."
              icon={<IconSearch size={18} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
              size="md"
              radius="md"
            />
            <Select
              placeholder="Sort by"
              value={sortBy}
              onChange={setSortBy}
              data={[
                { value: 'created_at', label: 'Date Created' },
                { value: 'name', label: 'Model Name' },
                { value: 'progress', label: 'Progress' },
                { value: 'status', label: 'Status' }
              ]}
              w={180}
              size="md"
              radius="md"
            />
          </Group>

          {/* Status Tabs - Using controlled mode */}
          <Tabs 
            value={activeTab} 
            onChange={(value) => {
              console.log('Tab clicked:', value);
              setActiveTab(value);
            }}
            variant="pills"
            radius="md"
          >
            <Tabs.List>
              <Tabs.Tab value="ongoing" color="blue">
                On-Going ({statusCounts.ongoing})
              </Tabs.Tab>
              <Tabs.Tab value="notstarted" color="gray">
                Not Started ({statusCounts.notstarted})
              </Tabs.Tab>
              <Tabs.Tab value="completed" color="green">
                Completed ({statusCounts.completed})
              </Tabs.Tab>
              <Tabs.Tab value="all">
                Show All ({statusCounts.all})
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </Card>

        {/* Enhanced Table */}
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Table highlightOnHover verticalSpacing="md" fontSize="sm">
            <thead style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <tr>
                <th style={{ padding: '16px 20px', fontWeight: 600, textAlign: 'left', minWidth: '200px' }}>Model Name</th>
                <th style={{ width: 110, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Progress</th>
                <th style={{ width: 150, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Status</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left', minWidth: '140px' }}>Current Phase</th>
                <th style={{ width: 130, padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Last Updated</th>
                <th style={{ width: 120, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>User</th>
                <th style={{ width: 90, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredModels.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <Center py={60}>
                      <Stack align="center" spacing="md">
                        <ThemeIcon size={64} radius="xl" variant="light" color="gray">
                          <IconAlertCircle size={32} />
                        </ThemeIcon>
                        <Text size="lg" weight={500} c="dimmed">
                          No assessments found
                        </Text>
                        <Text size="sm" c="dimmed">
                          {searchQuery 
                            ? `No results for "${searchQuery}"` 
                            : activeTab === 'all' 
                              ? 'No assessments available' 
                              : `No ${activeTab === 'ongoing' ? 'on-going' : activeTab === 'notstarted' ? 'not started' : 'completed'} assessments`
                          }
                        </Text>
                      </Stack>
                    </Center>
                  </td>
                </tr>
              ) : (
                filteredModels.map((model) => (
                  <React.Fragment key={model.modelId}>
                    <tr>
                      <td style={{ padding: '16px 20px', textAlign: 'left', maxWidth: '200px' }}>
                        <Tooltip label={model.modelName || model.model_name || 'Unnamed Model'} withArrow disabled={(model.modelName || model.model_name || '').length <= 30}>
                          <Text 
                            weight={600} 
                            size="sm"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '180px'
                            }}
                          >
                            {model.modelName || model.model_name || 'Unnamed Model'}
                          </Text>
                        </Tooltip>
                      </td>
                      
                      <td style={{ textAlign: 'center', padding: '16px 12px' }}>
                        <Center>
                          <ProgressRing progress={model.progress} size={45} />
                        </Center>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <StatusBadge status={model.assessmentStatus} />
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <Text size="sm" c="dimmed">
                          {getPositionLabel(model.position, model.step)}
                        </Text>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                        <Text size="sm" c="dimmed">
                          {getTimeSinceActivity(model.lastActivity)}
                        </Text>
                      </td>
                      
                      <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                        <Center>
                          <Tooltip label={model.lastActivityBy} withArrow>
                            <Avatar size={28} color="blue" radius="xl">
                              <IconUser size={16} />
                            </Avatar>
                          </Tooltip>
                        </Center>
                      </td>
                      
                      <td style={{ textAlign: 'center', padding: '16px 12px' }}>
                        <Center>
                          {model.assessmentStatus !== 'Completed' ? (
                            <Tooltip label={model.assessmentStatus === 'On-Going' ? 'Resume Assessment' : 'Start Assessment'} withArrow>
                              <ActionIcon 
                                variant="light" 
                                color="blue"
                                size="lg"
                                radius="md"
                                onClick={() => handleModelSelection(model)}
                              >
                                <IconPlayerPlay size={18} />
                              </ActionIcon>
                            </Tooltip>
                          ) : (
                            <Tooltip label="View Report" withArrow>
                              <ActionIcon 
                                variant="light" 
                                color="green"
                                size="lg"
                                radius="md"
                                onClick={() => setExpandedModel(
                                  expandedModel === model.modelId ? null : model.modelId
                                )}
                              >
                                {expandedModel === model.modelId ? 
                                  <IconChevronUp size={18} /> : 
                                  <IconChevronDown size={18} />
                                }
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Center>
                      </td>
                    </tr>
                    
                    {/* Expandable Report Row */}
                    {expandedModel === model.modelId && model.assessmentStatus === 'Completed' && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, backgroundColor: 'var(--mantine-color-gray-0)' }}>
                          <Collapse in={expandedModel === model.modelId}>
                            <Box p="lg">
                              <AssessmentReport
                                modelId={model.modelId}
                                assessmentUuid={model.assessmentUuid}
                              />
                            </Box>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      </Stack>

      {/* Mode Selection Dialog for New Assessments */}
      <StartAssessmentDialogV2 
        open={showModeDialog} 
        onClose={() => {
          setShowModeDialog(false);
          setSelectedModelForAssessment(null);
        }}
        preselectedModel={selectedModelForAssessment}
      />
    </Container>
  );
};

export default RiskAssessmentsMantine;
